// ./src/components/features/Donations/CausesList/CausesList.tsx
//
// ROADMAP #204 — the account's causes. A TABLE on desktop (name · where it is shown · raised of goal · gifts are taken ·
// receipts · actions), CARDS below it (swipe on phones, buttons on a tablet). Rows are never click-to-edit; the Edit
// button (or the swipe) is the only way in. The goal number is the server's aggregate (`raisedAmount`), never a sum of
// rows on screen. Shape of `MembershipTypesList`.
import React, { useCallback, useMemo } from "react";
import { Paper, Stack, Group, Text, Box, Badge, ActionIcon, Tooltip, Skeleton, Table, Progress, VisuallyHidden } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { STATUSES } from "@sdflc/utils";

import { Cause } from "@/utils/api";
import { useAppEnvironment } from "@/contexts";
import {
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  CardSkeletonActions,
  CardSkeletonLines,
  countCardActions,
  type CardSkeletonShape,
} from "@/components/generic";
import { formatCurrency, formatSmartDateTime, recordHasRight, HIGHLIGHT_STYLE, HIGHLIGHT_ROW_BG } from "@/utils";

export interface CausesListProps {
  causes: Cause[];
  /** The first read is back (an empty list then means "none", not "not yet"). REQUIRED — the list takes rows only as props. */
  isLoaded: boolean;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onEdit?: (cause: Cause) => void;
  onDelete?: (cause: Cause) => void;
  onAdd?: () => void;
  isRemoving: (id?: string) => boolean;
  highlightedId?: string | null;
  /** A search is narrowing the list: an empty result is "no match", never the first-run prompt. */
  hasQueryNarrowing: boolean;
  /** Organizer page names by id — "where it is shown". */
  pageNames: Map<string, string>;
}

/** What the "Gifts" column says: open now, not yet open, closed — from the cause's window. */
const windowState = (c: Cause, now: number): "open" | "upcoming" | "closed" => {
  if (c.startsAt && new Date(c.startsAt).getTime() > now) {
    return "upcoming";
  }

  if (c.endsAt && new Date(c.endsAt).getTime() <= now) {
    return "closed";
  }

  return "open";
};

const useLabels = () => {
  const { t } = useTranslation(["donations"]);

  const windowLabel = useCallback(
    (state: "open" | "upcoming" | "closed") =>
      state === "open"
        ? t("donations:causes.window.open", { defaultValue: "Taking gifts" })
        : state === "upcoming"
          ? t("donations:causes.window.upcoming", { defaultValue: "Not open yet" })
          : t("donations:causes.window.closed", { defaultValue: "Closed" }),
    [t]
  );

  const receiptLabel = useCallback(
    (kind?: string) =>
      kind === "official"
        ? t("donations:causes.receipt.official", { defaultValue: "Tax receipts" })
        : kind === "plain"
          ? t("donations:causes.receipt.plain", { defaultValue: "Plain receipts" })
          : t("donations:causes.receipt.none", { defaultValue: "No receipts" }),
    [t]
  );

  return { windowLabel, receiptLabel };
};

/** Raised of goal, with a bar when there is a goal. */
const GoalCell: React.FC<{ cause: Cause; language: string }> = ({ cause, language }) => {
  const { t } = useTranslation(["donations"]);
  const raised = formatCurrency(cause.raisedAmount ?? 0, cause.currencyCode, language);
  const goal = cause.goalAmount != null ? formatCurrency(cause.goalAmount, cause.currencyCode, language) : null;
  const pct = cause.goalAmount ? Math.min(100, ((cause.raisedAmount ?? 0) / cause.goalAmount) * 100) : null;

  return (
    <Stack gap={4}>
      <Text size="md">
        {goal
          ? t("donations:causes.raisedOfGoal", { defaultValue: "{{raised}} of {{goal}}", raised, goal })
          : t("donations:causes.raised", { defaultValue: "{{raised}} raised", raised })}
      </Text>
      {pct != null && <Progress value={pct} size="sm" radius="xl" aria-label={`${Math.round(pct)}%`} />}
      <Text size="sm" c="dimmed">
        {t("donations:causes.giftsCount", { defaultValue: "{{count}} gifts", count: cause.donationsCount ?? 0 })}
      </Text>
    </Stack>
  );
};

/** Disabled / receipt details missing — what the host must notice. */
const CauseBadges: React.FC<{ cause: Cause }> = ({ cause }) => {
  const { t } = useTranslation(["donations", "common"]);
  const missing = (cause.receiptMissing ?? []).length > 0;

  return (
    <>
      {cause.status === STATUSES.DISABLED && (
        <Badge size="sm" variant="light" color="gray">
          {t("common:status.disabled", { defaultValue: "Disabled" })}
        </Badge>
      )}
      {missing && (
        <Tooltip label={t("donations:causes.receiptMissingHint", { defaultValue: "Fill in the receipt details before tax receipts can be given." })} withArrow>
          <Badge size="sm" variant="light" color="orange">
            {t("donations:causes.receiptMissing", { defaultValue: "Receipt details incomplete" })}
          </Badge>
        </Tooltip>
      )}
    </>
  );
};

const RowActions: React.FC<{ cause: Cause; onEdit?: (c: Cause) => void; onDelete?: (c: Cause) => void; isDeleting: boolean }> = ({
  cause,
  onEdit,
  onDelete,
  isDeleting,
}) => {
  const { t } = useTranslation(["common"]);
  const showEdit = !!onEdit && recordHasRight(cause, "edit");
  const showDelete = !!onDelete && recordHasRight(cause, "remove");

  if (!showEdit && !showDelete) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      {showEdit && onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon variant="subtle" color="blue" size="md" onClick={() => onEdit(cause)} disabled={isDeleting} aria-label={t("common:buttons.edit")}>
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={() => onDelete(cause)}
            loading={isDeleting}
            disabled={isDeleting}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

interface RowProps {
  cause: Cause;
  onEdit?: (c: Cause) => void;
  onDelete?: (c: Cause) => void;
  highlighted: boolean;
  isDeleting: boolean;
  language: string;
  pageName: string;
  windowText: string;
  receiptText: string;
}

const CauseTableRow: React.FC<RowProps> = ({ cause, onEdit, onDelete, highlighted, isDeleting, language, pageName, windowText, receiptText }) => (
  <Table.Tr
    style={{
      opacity: isDeleting ? 0.5 : cause.status === STATUSES.DISABLED ? 0.6 : 1,
      backgroundColor: highlighted ? HIGHLIGHT_ROW_BG : undefined,
      transition: "background-color 150ms ease, opacity 150ms ease",
    }}
  >
    <Table.Td>
      <Group gap={6} wrap="wrap">
        <Text size="md" fw={500} lineClamp={1}>
          {cause.name || "—"}
        </Text>
        <CauseBadges cause={cause} />
      </Group>
      <Text size="sm" c="dimmed" lineClamp={1}>
        {pageName}
      </Text>
    </Table.Td>
    <Table.Td style={{ width: 240 }}>
      <GoalCell cause={cause} language={language} />
    </Table.Td>
    <Table.Td style={{ width: 200 }}>
      <Text size="md">{windowText}</Text>
      {cause.endsAt && (
        <Text size="sm" c="dimmed">
          {formatSmartDateTime(cause.endsAt, language)}
        </Text>
      )}
    </Table.Td>
    <Table.Td style={{ width: 160 }}>
      <Text size="md">{receiptText}</Text>
    </Table.Td>
    <Table.Td style={{ width: 96 }}>
      <RowActions cause={cause} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />
    </Table.Td>
  </Table.Tr>
);

const CauseTableRowSkeleton: React.FC = () => (
  <Table.Tr>
    <Table.Td>
      <Skeleton height={18} width="60%" maw={260} />
      <Skeleton height={14} width="35%" maw={160} mt={6} />
    </Table.Td>
    <Table.Td style={{ width: 240 }}>
      <Skeleton height={16} width={160} />
      <Skeleton height={8} width={200} mt={6} radius="xl" />
      <Skeleton height={14} width={70} mt={6} />
    </Table.Td>
    <Table.Td style={{ width: 200 }}>
      <Skeleton height={16} width={110} />
    </Table.Td>
    <Table.Td style={{ width: 160 }}>
      <Skeleton height={16} width={100} />
    </Table.Td>
    <Table.Td style={{ width: 96 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const CauseCardComponent: React.FC<CardProps> = (props) => {
  const { cause, onEdit, onDelete, highlighted, isDeleting, language, pageName, windowText, receiptText, forceClose, onForceCloseComplete, onSwipeStateChange } = props;
  const { t } = useTranslation(["common"]);

  // Edit / remove are IDENTITY actions — the right side (swipe the card left).
  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (onEdit && recordHasRight(cause, "edit")) {
      actions.push({ key: "edit", icon: "Edit", label: t("common:buttons.edit"), color: "blue", onClick: () => onEdit(cause), disabled: isDeleting });
    }

    if (onDelete && recordHasRight(cause, "remove")) {
      actions.push({ key: "delete", icon: "Trash", label: t("common:buttons.delete"), color: "red", onClick: () => onDelete(cause), loading: isDeleting, disabled: isDeleting });
    }

    return actions;
  }, [onEdit, onDelete, cause, isDeleting, t]);

  const cardBody = (showActions: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : cause.status === STATUSES.DISABLED ? 0.6 : 1}
      style={{ transition: "opacity 150ms ease", ...(highlighted ? HIGHLIGHT_STYLE : {}) }}
    >
      <Group gap="md" wrap="nowrap" align="flex-start">
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="wrap">
            <Text size="md" fw={500} lineClamp={1}>
              {cause.name || "—"}
            </Text>
            <CauseBadges cause={cause} />
          </Group>
          <Text size="sm" c="dimmed" lineClamp={1}>
            {pageName} · {windowText} · {receiptText}
          </Text>
          <Box mt="xs">
            <GoalCell cause={cause} language={language} />
          </Box>
        </Box>
        {showActions && <RowActions cause={cause} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />}
      </Group>
    </Paper>
  );

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false)}
          </SwipeableCard>
        ) : (
          cardBody(false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true)}</Box>
    </>
  );
};

const CauseCard = React.memo(CauseCardComponent);

const CauseCardSkeleton: React.FC<CardSkeletonShape> = ({ actions }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="flex-start">
      <Box style={{ flex: 1, minWidth: 0 }}>
        <CardSkeletonLines count={2} sizes={["md", "sm"]} gap={2} />
        <Skeleton height={8} width="80%" mt="sm" radius="xl" />
        <Skeleton height={14} width={70} mt={6} />
      </Box>
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

const CausesListComponent: React.FC<CausesListProps> = ({
  causes,
  isLoaded,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  onEdit,
  onDelete,
  onAdd,
  isRemoving,
  highlightedId,
  hasQueryNarrowing,
  pageNames,
}) => {
  const { t, i18n } = useTranslation(["donations", "common"]);
  const { isDesktop } = useAppEnvironment();
  const language = i18n.language;
  const { windowLabel, receiptLabel } = useLabels();
  const cardActions = countCardActions(onEdit, onDelete);
  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();
  const now = Date.now();

  const handleEdit = useCallback(
    (c: Cause) => {
      closeAllCards();
      onEdit?.(c);
    },
    [onEdit, closeAllCards]
  );

  const handleDelete = useCallback(
    (c: Cause) => {
      closeAllCards();
      onDelete?.(c);
    },
    [onDelete, closeAllCards]
  );

  // ⚠ A SKELETON IS FOR THE FIRST READ ONLY (▶ check-list-states).
  const showSkeleton = !isLoaded;

  if (!showSkeleton && causes.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("donations:causes.noMatch", { defaultValue: "No causes match your search." })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("donations:causes.empty.title", { defaultValue: "No causes yet" })}
          description={t("donations:causes.empty.description", {
            defaultValue: "A cause is what people give to — a roof fund, scholarships, the season. Add one to take gifts on your organizer page.",
          })}
          iconName="Heart"
          actionLabel={t("donations:causes.empty.action", { defaultValue: "Add cause" })}
          onAction={onAdd}
          size="md"
        />
      </Paper>
    );
  }

  const rowProps = (c: Cause): RowProps => ({
    cause: c,
    onEdit: onEdit ? handleEdit : undefined,
    onDelete: onDelete ? handleDelete : undefined,
    highlighted: c.id === highlightedId,
    isDeleting: isRemoving(c.id),
    language,
    pageName: c.organizerProfileId
      ? pageNames.get(c.organizerProfileId) ?? t("donations:causes.onePage", { defaultValue: "One organizer page" })
      : t("donations:causes.allPages", { defaultValue: "Every organizer page" }),
    windowText: windowLabel(windowState(c, now)),
    receiptText: receiptLabel(c.receiptKind),
  });

  const loadMore = <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />;

  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("donations:causes.columns.name", { defaultValue: "Cause" })}</Table.Th>
                <Table.Th style={{ width: 240 }}>{t("donations:causes.columns.raised", { defaultValue: "Raised" })}</Table.Th>
                <Table.Th style={{ width: 200 }}>{t("donations:causes.columns.window", { defaultValue: "Gifts" })}</Table.Th>
                <Table.Th style={{ width: 160 }}>{t("donations:causes.columns.receipts", { defaultValue: "Receipts" })}</Table.Th>
                <Table.Th style={{ width: 96 }}>
                  <VisuallyHidden>{t("common:labels.actions", { defaultValue: "Actions" })}</VisuallyHidden>
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 5 }).map((_, i) => <CauseTableRowSkeleton key={i} />)
                : causes.map((c) => <CauseTableRow key={c.id} {...rowProps(c)} />)}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 4 }).map((_, i) => (
          <CauseCardSkeleton key={i} actions={cardActions} withCheckbox={false} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="sm">
      {causes.map((c) => (
        <CauseCard
          key={c.id}
          {...rowProps(c)}
          forceClose={shouldForceClose(c.id || "")}
          onForceCloseComplete={() => handleForceCloseComplete(c.id || "")}
          onSwipeStateChange={(isOpen) => {
            if (isOpen) {
              openCard(c.id || "");
            }
          }}
        />
      ))}
      {loadMore}
    </Stack>
  );
};

export const CausesList = React.memo(CausesListComponent);
